import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, ExternalLink, Sparkles, RefreshCw, Signal, Mail, Phone,
  Linkedin, ChevronDown, ChevronRight, Clock, ShieldOff, Ban, Zap, Search, Trash2, Wand2, Copy, Save, Star, Plus, Pencil, X, Users, CheckCircle2, CalendarClock,
} from "lucide-react";
import api, { freshnessLabel, formatApiError, taskDueInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

function Section({ title, children, right }) {
  return (
    <div className="bg-white border border-line rounded-[8px] p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stages, setStages] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [note, setNote] = useState("");
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [excludeReason, setExcludeReason] = useState("");
  const [enriching, setEnriching] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [emailModal, setEmailModal] = useState({ open: false, loading: false, dmId: null, dmName: "", subject: "", body: "" });
  const [draftLoading, setDraftLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualDm, setManualDm] = useState({ first_name: "", last_name: "", role: "", linkedin_url: "", email: "", phone: "" });
  const [addingDm, setAddingDm] = useState(false);
  const [users, setUsers] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);
  const [taskForm, setTaskForm] = useState({ date: "", time: "", note: "" });
  const [addingTask, setAddingTask] = useState(false);

  const load = useCallback(() => {
    api.get(`/opportunities/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento"));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/pipeline/stages").then((r) => setStages(r.data));
    api.get("/pipeline/loss-reasons").then((r) => setLossReasons(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [load]);

  if (!data) return <p className="text-sm text-subtle">Caricamento…</p>;

  const { opportunity: o, company: c, signals, decision_makers: dms, hiring_team: hiringTeam = [], history, follower_users: followerUsers = [], assignee_gmail: assigneeGmail = { connected: false } } = data;
  const stageObj = stages.find((s) => s.key === o.stage);
  const closed = o.esito !== "aperto";

  const changeStage = async (v) => {
    try {
      await api.post(`/opportunities/${id}/stage`, { stage: v });
      toast.success("Fase aggiornata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reassign = async (v) => {
    try {
      await api.patch(`/opportunities/${id}/assign-to`, { user_id: v === "none" ? null : v });
      toast.success(v === "none" ? "Assegnazione rimossa" : "Opportunità assegnata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addFollower = async (uid) => {
    try {
      await api.post(`/opportunities/${id}/followers`, { user_id: uid });
      toast.success("Follower aggiunto");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeFollower = async (uid) => {
    try {
      await api.delete(`/opportunities/${id}/followers/${uid}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCompanyEdit = () => {
    setCompanyForm({
      name: c.name || "", vat: c.vat || "", industry: c.industry || "",
      size: c.size != null ? String(c.size) : "",
      zone: c.zone || "", website: c.website || "", domain: c.domain || "",
      linkedin_url: c.linkedin_url || "", notes: c.notes || "",
    });
    setCompanyOpen(true);
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.patch(`/companies/${c.id}`, companyForm);
      toast.success("Dati azienda aggiornati");
      setCompanyOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setSavingCompany(false); }
  };

  const closeLost = async () => {
    if (!lostReason) return toast.error("Seleziona un motivo");
    try {
      await api.post(`/opportunities/${id}/close-lost`, { reason: lostReason, note: lostNote });
      toast.success("Opportunità chiusa come persa");
      setLostOpen(false); setLostReason(""); setLostNote(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeEsito = async (v) => {
    if (v === o.esito) return;
    if (v === "perso") { setLostOpen(true); return; }
    try {
      await api.post(`/opportunities/${id}/set-esito`, { esito: v });
      toast.success(v === "aperto" ? "Opportunità riaperta" : "Segnata come Vinta");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/opportunities/${id}/note`, { text: note });
    setNote(""); load();
  };

  const addTask = async () => {
    if (!taskForm.date || !taskForm.time) return toast.error("Inserisci data e ora");
    const due = new Date(`${taskForm.date}T${taskForm.time}`);
    if (isNaN(due.getTime())) return toast.error("Data/ora non valida");
    setAddingTask(true);
    try {
      await api.post(`/opportunities/${id}/tasks`, { due_at: due.toISOString(), note: taskForm.note });
      toast.success("Task creato");
      setTaskForm({ date: "", time: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingTask(false); }
  };

  const completeTask = async (tid) => {
    try {
      await api.post(`/opportunities/${id}/tasks/${tid}/complete`);
      toast.success("Task completato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteTask = async (tid) => {
    try {
      await api.delete(`/opportunities/${id}/tasks/${tid}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const enrich = async (dmId) => {
    setEnriching(dmId);
    try {
      const { data } = await api.post(`/decision-makers/${dmId}/enrich`);
      if (data.email && data.phone) toast.success("Contatto arricchito (email + telefono)");
      else if (data.fullenrich_pending) toast.info("Prospeo parziale → fallback FullEnrich in corso, aggiorno tra poco…");
      else if (data.email || data.phone) toast.success("Contatto arricchito");
      else toast.info("Nessun contatto trovato per questo decision maker");
      await load();
      if (data.fullenrich_pending) pollFullenrich(dmId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEnriching(null); }
  };

  const addFromHiringTeam = async (p) => {
    try {
      await api.post(`/opportunities/${id}/hiring-team/add`, { full_name: p.full_name, role: p.role, linkedin_url: p.linkedin_url });
      toast.success(`${p.full_name} aggiunto ai Decision Maker`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pollFullenrich = (dmId) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const { data: d } = await api.get(`/opportunities/${id}`);
        setData(d);
        const dm = (d.decision_makers || []).find((x) => x.id === dmId);
        const done = dm?.fullenrich?.status === "done";
        if (done || tries >= 20) {
          clearInterval(iv);
          if (done) {
            if (dm.email || dm.phone) toast.success("FullEnrich: contatto aggiornato");
            else toast.info("FullEnrich: nessun dato aggiuntivo trovato");
          }
        }
      } catch { clearInterval(iv); }
    }, 6000);
  };

  const addManualDm = async () => {
    if (!manualDm.first_name.trim() && !manualDm.last_name.trim()) return toast.error("Inserisci almeno nome o cognome");
    setAddingDm(true);
    try {
      await api.post(`/opportunities/${id}/decision-makers/manual`, manualDm);
      toast.success("Decision maker aggiunto");
      setManualOpen(false);
      setManualDm({ first_name: "", last_name: "", role: "", linkedin_url: "", email: "", phone: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setAddingDm(false); }
  };

  const discover = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post(`/opportunities/${id}/discover-dms`);
      if (data.found > 0) {
        if (data.provider === "pdl") toast.success(`${data.found} decision maker trovati via People Data Labs (fallback)`);
        else if (data.used_role_fallback) toast.info(`${data.found} contatti trovati: nessun match sui ruoli target, mostro le figure manageriali dell'azienda`);
        else toast.success(`${data.found} decision maker trovati`);
      } else toast.info("Nessun decision maker trovato per questa azienda");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDiscovering(false); }
  };

  const optOut = async (dmId) => {
    await api.post(`/decision-makers/${dmId}/opt-out`);
    toast.success("Opt-out registrato");
    load();
  };

  const deleteOpp = async () => {
    if (!window.confirm(`Eliminare definitivamente l'opportunità di "${c.name}"? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Opportunità eliminata");
      navigate("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEmail = (dm) => {
    const dmName = `${dm.first_name || ""} ${dm.last_name || ""}`.trim();
    const existing = dm.cold_emails?.[id];
    if (existing) {
      setEmailModal({ open: true, loading: false, dmId: dm.id, dmName, subject: existing.subject || "", body: existing.body || "" });
    } else {
      generateEmail(dm);
    }
  };

  const generateEmail = async (dm) => {
    const dmName = `${dm.first_name || ""} ${dm.last_name || ""}`.trim();
    setEmailModal({ open: true, loading: true, dmId: dm.id, dmName, subject: "", body: "" });
    try {
      const { data } = await api.post(`/opportunities/${id}/decision-makers/${dm.id}/cold-email`);
      setEmailModal((m) => ({ ...m, loading: false, subject: data.subject || "", body: data.body || "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione email fallita");
      setEmailModal((m) => ({ ...m, open: false, loading: false }));
    }
  };

  const regenerateEmail = async () => {
    if (!emailModal.dmId) return;
    if (!window.confirm("Rigenerare l'email? La versione attuale verrà sostituita.")) return;
    setEmailModal((m) => ({ ...m, loading: true }));
    try {
      const { data } = await api.post(`/opportunities/${id}/decision-makers/${emailModal.dmId}/cold-email`);
      setEmailModal((m) => ({ ...m, loading: false, subject: data.subject || "", body: data.body || "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione email fallita");
      setEmailModal((m) => ({ ...m, loading: false }));
    }
  };

  const saveEmail = async () => {
    if (!emailModal.dmId) return;
    try {
      await api.put(`/opportunities/${id}/decision-makers/${emailModal.dmId}/cold-email`, {
        subject: emailModal.subject, body: emailModal.body,
      });
      toast.success("Email salvata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    }
  };

  const createDraft = async () => {
    if (!emailModal.dmId) return;
    setDraftLoading(true);
    try {
      const { data: r } = await api.post(`/opportunities/${id}/decision-makers/${emailModal.dmId}/gmail-draft`, {
        subject: emailModal.subject, body: emailModal.body,
      });
      toast.success(`Bozza creata in Gmail (${r.account_email}) → ${r.recipient}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Creazione bozza fallita");
    } finally {
      setDraftLoading(false);
    }
  };

  const copyEmail = async () => {
    const text = `Oggetto: ${emailModal.subject}\n\n${emailModal.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copiata negli appunti");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const deleteDm = async (dm) => {
    if (!window.confirm(`Eliminare il decision maker "${dm.first_name} ${dm.last_name}"?`)) return;
    try {
      await api.delete(`/decision-makers/${dm.id}`);
      toast.success("Decision maker eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const excludeCompany = async () => {
    if (!excludeReason) return toast.error("Motivo obbligatorio");
    await api.post(`/admin/companies/${c.id}/exclude`, { reason: excludeReason });
    toast.success("Azienda aggiunta alla Lista Esclusioni");
    setExcludeOpen(false); load();
  };

  return (
    <div className="flex flex-col gap-6 max-w-6xl">
      <button data-testid="back-button" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-subtle hover:text-ink transition-colors w-fit">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Indietro
      </button>

      {/* Header */}
      <div className="bg-white border border-line rounded-[8px] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-[8px] bg-brand-soft flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-brand-dark" strokeWidth={1.5} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-semibold tracking-tight text-ink">{c.name}</h1>
                <span className="text-sm text-faint">Opp #{o.number}</span>
                {o.is_reactivation && (
                  <span className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2 py-0.5 text-[11px] font-medium">
                    <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Riattivata
                  </span>
                )}
                {o.esito === "vinto" && <span className="bg-brand text-ink rounded-full px-2.5 py-0.5 text-[11px] font-semibold">VINTO</span>}
                {o.esito === "perso" && <span className="bg-destructive text-white rounded-full px-2.5 py-0.5 text-[11px] font-semibold">PERSO</span>}
                {c.excluded && <span className="inline-flex items-center gap-1 bg-[#FFE5E5] text-destructive rounded-full px-2 py-0.5 text-[11px] font-medium"><Ban className="w-3 h-3" strokeWidth={1.5} /> Esclusa</span>}
              </div>
              <p className="text-xs text-subtle mt-1">
                {o.position_name} · {c.industry || "settore n/d"} · {c.zone || "zona n/d"}
              </p>
              {o.loss_reason && <p className="text-xs text-destructive mt-1">Motivo perdita: {o.loss_reason}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-subtle">Stato</span>
              <Select value={o.esito} onValueChange={changeEsito}>
                <SelectTrigger data-testid="esito-select" className="w-36 h-9 bg-white border-line rounded-[6px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-line">
                  <SelectItem value="aperto">Aperto</SelectItem>
                  <SelectItem value="vinto">Vinto</SelectItem>
                  <SelectItem value="perso">Perso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!closed && (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-subtle">Assegnata a</span>
                  <Select value={o.assigned_to || "none"} onValueChange={reassign}>
                    <SelectTrigger data-testid="assignee-select" className="w-48 h-9 bg-white border-line rounded-[6px] text-sm">
                      <SelectValue placeholder="Non assegnato" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-line">
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.role === "admin" ? " (admin)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Select value={o.stage} onValueChange={changeStage}>
                  <SelectTrigger data-testid="stage-select" className="w-44 h-9 bg-white border-line rounded-[6px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-line">
                    {stages.map((s) => <SelectItem key={s.id} value={s.key}>{s.name}{s.marks_won ? " (→ Vinto)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-subtle">Follower</span>
            <Select value="" onValueChange={addFollower}>
              <SelectTrigger data-testid="add-follower-select" className="w-44 h-9 bg-white border-line rounded-[6px] text-sm">
                <SelectValue placeholder="Aggiungi follower" />
              </SelectTrigger>
              <SelectContent className="bg-white border-line">
                {users.filter((u) => u.id !== o.assigned_to && !(o.followers || []).includes(u.id)).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}{u.role === "admin" ? " (admin)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-line text-xs text-subtle">
          <span>Fase: <b className="text-ink">{stageObj?.name || o.stage}</b></span>
          <span>Sales: <b className="text-ink">{o.assigned_to_name || "Non assegnato"}</b></span>
          {followerUsers.length > 0 && (
            <span className="flex items-center gap-1 flex-wrap" data-testid="follower-list">
              <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
              {followerUsers.map((f) => (
                <span key={f.id} data-testid={`follower-chip-${f.id}`} className="inline-flex items-center gap-1 bg-brand-soft text-brand-dark rounded-full px-2 py-0.5 text-[11px] font-medium">
                  {f.name}
                  <button data-testid={`remove-follower-${f.id}`} onClick={() => removeFollower(f.id)} className="hover:text-destructive transition-colors" title="Rimuovi follower">
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </span>
          )}
          <div className="ml-auto flex items-center gap-4">
            {user?.role === "admin" && !c.excluded && (
              <button data-testid="exclude-company-button" onClick={() => setExcludeOpen(true)} className="flex items-center gap-1 text-destructive hover:underline">
                <Ban className="w-3.5 h-3.5" strokeWidth={1.5} /> Escludi azienda
              </button>
            )}
            <button data-testid="delete-opp-button" onClick={deleteOpp} className="flex items-center gap-1 text-destructive hover:underline">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Elimina opportunità
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Company */}
          <Section title="Dati azienda" right={
            <button data-testid="edit-company-button" onClick={openCompanyEdit} className="flex items-center gap-1.5 bg-white border border-line text-ink rounded-[6px] px-3 py-1.5 text-xs font-medium hover:bg-background transition-colors">
              <Pencil className="w-3.5 h-3.5 text-brand-dark" strokeWidth={1.5} /> Modifica
            </button>
          }>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field label="Ragione sociale" value={c.name} />
              <Field label="P.IVA" value={c.vat} />
              <Field label="Settore" value={c.industry} />
              <Field label="Dimensione" value={c.size} />
              <Field label="Zona" value={c.zone} />
              <Field label="Sito" value={c.website} link={c.website} />
              <Field label="LinkedIn" value={c.linkedin_url ? "Profilo aziendale" : ""} link={c.linkedin_url} />
              <Field label="Dominio" value={c.domain} />
            </div>
            {!c.domain && (
              <p className="mt-3 text-xs bg-[#FFF4E5] text-[#B45309] rounded-[6px] p-3">
                ⚠️ Dominio/sito mancante: aggiungilo con "Modifica" per abilitare la ricerca automatica dei Decision Maker ("Trova DM").
              </p>
            )}
            {c.notes && <p className="mt-3 text-xs bg-background rounded-[6px] p-3 text-subtle">Note generali: {c.notes}</p>}
          </Section>

          {/* Signals */}
          <Section title={`Segnali associati (${signals.length})`}>
            <div className="flex flex-col gap-2">
              {signals.map((s) => <SignalRow key={s.id} s={s} />)}
              {signals.length === 0 && <p className="text-xs text-faint">Nessun segnale.</p>}
            </div>
          </Section>

          {/* Hiring Team (dagli annunci TheirStack) */}
          {hiringTeam.length > 0 && (
            <Section title={`Hiring Team (${hiringTeam.length})`}>
              <p className="text-[11px] text-faint mb-3">Persone che hanno pubblicato l'annuncio, rilevate da TheirStack. Gratis, nessun credito.</p>
              <div className="flex flex-col gap-2">
                {hiringTeam.map((p, i) => (
                  <div key={i} data-testid={`hiring-team-${i}`} className="flex items-center gap-3 border border-line rounded-[8px] p-3">
                    {p.image_url
                      ? <img src={p.image_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                      : <div className="w-9 h-9 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center text-xs font-semibold shrink-0">{(p.full_name || "?").slice(0, 1)}</div>}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink truncate">{p.full_name}</p>
                      <p className="text-xs text-subtle truncate">{p.role || "Ruolo n/d"}</p>
                    </div>
                    {p.linkedin_url && (
                      <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] text-[#0A66C2] hover:underline shrink-0" title="Apri profilo LinkedIn">
                        <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} /> LinkedIn
                      </a>
                    )}
                    {p.already_dm ? (
                      <span className="text-[11px] text-faint shrink-0">Già nei DM</span>
                    ) : !closed && (
                      <button data-testid={`add-hiring-${i}`} onClick={() => addFromHiringTeam(p)} className="flex items-center gap-1 text-[11px] bg-brand text-ink font-medium rounded-[6px] px-2 py-1 hover:bg-brand-hover transition-colors shrink-0">
                        <Search className="w-3 h-3" strokeWidth={1.5} /> Aggiungi ai DM
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Decision Makers */}
          <Section title={`Decision Maker candidati (${dms.length})`} right={
            !closed && (
              <div className="flex items-center gap-2">
                <a
                  data-testid="linkedin-search-button"
                  href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(c.name)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-white border border-line text-ink rounded-[6px] px-3 py-1.5 text-xs font-medium hover:bg-background transition-colors"
                  title="Cerca le persone dell'azienda su LinkedIn"
                >
                  <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" strokeWidth={1.5} /> Cerca su LinkedIn
                </a>
                <button data-testid="discover-dms-button" onClick={discover} disabled={discovering} className="flex items-center gap-1.5 bg-white border border-line text-ink rounded-[6px] px-3 py-1.5 text-xs font-medium hover:bg-background disabled:opacity-50 transition-colors">
                  <Search className="w-3.5 h-3.5 text-brand-dark" strokeWidth={1.5} /> {discovering ? "Ricerca…" : "Trova DM"}
                </button>
                <button data-testid="add-manual-dm-button" onClick={() => setManualOpen(true)} className="flex items-center gap-1.5 bg-brand text-ink rounded-[6px] px-3 py-1.5 text-xs font-medium hover:bg-brand-hover transition-colors">
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Aggiungi manualmente
                </button>
              </div>
            )
          }>
            <div className="flex flex-col gap-2 max-h-[560px] overflow-y-auto pr-1">
              {dms.map((dm) => (
                <div key={dm.id} data-testid={`dm-${dm.id}`} className="border border-line rounded-[6px] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-ink flex items-center gap-1">
                        {dm.recommended && (
                          <Star data-testid={`dm-star-${dm.id}`} className="w-3.5 h-3.5 text-[#F5A623] fill-[#F5A623] shrink-0" strokeWidth={1.5} title="Coerente con la posizione: potenzialmente più indicato per il contatto" />
                        )}
                        {dm.first_name} {dm.last_name} {dm.opt_out && <span className="text-[10px] bg-[#FFE5E5] text-destructive rounded-full px-1.5 py-0.5 ml-1">opt-out</span>}
                      </p>
                      <p className="text-xs text-subtle">{dm.role} · {dm.seniority}</p>
                      {dm.fullenrich?.status === "pending" && (
                        <span data-testid={`fe-pending-${dm.id}`} className="inline-flex items-center gap-1 bg-[#FFF4E5] text-[#B45309] rounded-full px-2 py-0.5 text-[11px] font-medium mt-1">
                          <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={1.5} /> Ricerca telefono via FullEnrich…
                        </span>
                      )}
                      {dm.fullenrich?.status === "not_configured" && (
                        <span data-testid={`fe-notconfig-${dm.id}`} className="inline-flex items-center gap-1 bg-[#FFE5E5] text-destructive rounded-full px-2 py-0.5 text-[11px] font-medium mt-1">
                          FullEnrich non configurato (chiave mancante)
                        </span>
                      )}
                      {dm.fullenrich?.status === "error" && (
                        <span data-testid={`fe-error-${dm.id}`} className="inline-flex items-center gap-1 bg-[#FFE5E5] text-destructive rounded-full px-2 py-0.5 text-[11px] font-medium mt-1" title={dm.fullenrich?.detail || ""}>
                          Errore FullEnrich
                        </span>
                      )}
                      {dm.linkedin_url && (
                        <a href={dm.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-dark hover:underline mt-1">
                          <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} /> LinkedIn
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {!dm.opt_out && (
                        <button
                          data-testid={`enrich-${dm.id}`}
                          onClick={() => enrich(dm.id)}
                          disabled={enriching === dm.id}
                          className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-3 py-1.5 text-xs hover:bg-brand-hover disabled:opacity-50 transition-colors"
                        >
                          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {enriching === dm.id ? "Arricchimento…" : dm.enriched ? "Ri-arricchisci" : "Arricchisci contatto"}
                        </button>
                      )}
                      {!dm.opt_out && (
                        <button
                          data-testid={`gen-email-${dm.id}`}
                          onClick={() => openEmail(dm)}
                          disabled={emailModal.loading && emailModal.dmId === dm.id}
                          className="flex items-center gap-1.5 border border-brand-dark text-brand-dark font-medium rounded-[6px] px-3 py-1.5 text-xs hover:bg-brand/10 disabled:opacity-50 transition-colors"
                        >
                          <Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          {(emailModal.loading && emailModal.dmId === dm.id) ? "Genero…" : (dm.cold_emails?.[id] ? "Cold email" : "Genera cold email")}
                        </button>
                      )}
                      <button data-testid={`optout-${dm.id}`} onClick={() => optOut(dm.id)} className="flex items-center gap-1 text-[11px] text-faint hover:text-destructive transition-colors">
                        <ShieldOff className="w-3 h-3" strokeWidth={1.5} /> Opt-out
                      </button>
                      <button data-testid={`delete-dm-${dm.id}`} onClick={() => deleteDm(dm)} className="flex items-center gap-1 text-[11px] text-faint hover:text-destructive transition-colors">
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Elimina
                      </button>
                    </div>
                  </div>
                  {dm.enriched && (
                    <div className="mt-2 pt-2 border-t border-line flex flex-wrap gap-4 text-xs">
                      <span className="inline-flex items-center gap-1.5 text-ink"><Mail className="w-3.5 h-3.5 text-brand-dark" strokeWidth={1.5} /> {dm.email || <span className="text-faint">email non trovata</span>}</span>
                      <span className="inline-flex items-center gap-1.5 text-ink"><Phone className="w-3.5 h-3.5 text-brand-dark" strokeWidth={1.5} /> {dm.phone || <span className="text-faint">telefono non trovato</span>}</span>
                      {dm.enrichment && <span className="text-faint">via {dm.enrichment.fallback ? "Prospeo + FullEnrich" : (dm.enrichment.provider || "Prospeo")} · {dm.enrichment.cost_eur}€</span>}
                    </div>
                  )}
                </div>
              ))}
              {dms.length === 0 && <p className="text-xs text-faint">Nessun decision maker. Usa "Trova DM" per cercarli sull'azienda (consuma 1 credito).</p>}
            </div>
          </Section>

          {/* Company history */}
          {(history.opportunities.length > 0 || history.signals.length > signals.length) && (
            <Collapsible className="bg-white border border-line rounded-[8px]">
              <CollapsibleTrigger data-testid="history-toggle" className="w-full flex items-center justify-between p-5 group">
                <span className="text-sm font-semibold text-ink">Storico Azienda ({history.opportunities.length} opp. precedenti)</span>
                <ChevronDown className="w-4 h-4 text-subtle group-data-[state=open]:rotate-180 transition-transform" strokeWidth={1.5} />
              </CollapsibleTrigger>
              <CollapsibleContent className="px-5 pb-5">
                <div className="flex flex-col gap-2">
                  {history.opportunities.map((h) => (
                    <div key={h.id} className="border border-line rounded-[6px] p-3 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-ink">Opp #{h.number}</span>
                        {h.esito === "vinto" && <span className="bg-brand text-ink rounded-full px-2 py-0.5 font-semibold">VINTO</span>}
                        {h.esito === "perso" && <span className="bg-destructive text-white rounded-full px-2 py-0.5 font-semibold">PERSO</span>}
                        {h.esito === "aperto" && <span className="bg-background border border-line rounded-full px-2 py-0.5">Aperto</span>}
                        <span className="text-faint ml-auto">{h.assigned_to_name || "n/a"}</span>
                      </div>
                      {h.loss_reason && <p className="text-destructive mt-1">Motivo: {h.loss_reason}</p>}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Timeline + notes */}
        <div className="flex flex-col gap-6">
          <Section title="Task">
            <div className="flex flex-col gap-2 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="task-date" type="date" value={taskForm.date} onChange={(e) => setTaskForm((f) => ({ ...f, date: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
                <input data-testid="task-time" type="time" value={taskForm.time} onChange={(e) => setTaskForm((f) => ({ ...f, time: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
              <input data-testid="task-note" value={taskForm.note} onChange={(e) => setTaskForm((f) => ({ ...f, note: e.target.value }))} placeholder="Nota (es. Richiamare il titolare)" className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              <button data-testid="add-task-button" onClick={addTask} disabled={addingTask} className="flex items-center justify-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
                <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} /> {addingTask ? "Salvo…" : "Aggiungi task"}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {[...(o.tasks || [])].sort((a, b) => (a.completed - b.completed) || (new Date(a.due_at) - new Date(b.due_at))).map((t) => {
                const info = taskDueInfo(t.due_at);
                return (
                  <div key={t.id} data-testid={`task-${t.id}`} className={`border rounded-[6px] p-2.5 flex items-start gap-2 ${t.completed ? "border-line bg-background opacity-70" : info?.urgent ? "border-destructive/40 bg-[#FFF5F5]" : "border-line"}`}>
                    <button data-testid={`complete-task-${t.id}`} onClick={() => !t.completed && completeTask(t.id)} disabled={t.completed} className={`shrink-0 mt-0.5 ${t.completed ? "text-brand-dark" : "text-faint hover:text-brand-dark"} transition-colors`} title={t.completed ? "Completato" : "Segna come completato"}>
                      <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-ink">{new Date(t.due_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        {!t.completed && info && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${info.urgent ? "bg-[#FFE5E5] text-destructive animate-pulse" : "bg-brand-soft text-brand-dark"}`}>
                            <Clock className="w-3 h-3" strokeWidth={1.5} /> {info.overdue ? "scaduto" : info.label}
                          </span>
                        )}
                        {t.completed && <span className="text-[11px] text-brand-dark font-medium">Completato</span>}
                      </div>
                      {t.note && <p className={`text-xs mt-1 ${t.completed ? "text-faint line-through" : "text-subtle"}`}>{t.note}</p>}
                    </div>
                    <button data-testid={`delete-task-${t.id}`} onClick={() => deleteTask(t.id)} className="shrink-0 text-faint hover:text-destructive transition-colors" title="Elimina task">
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                );
              })}
              {(o.tasks || []).length === 0 && <p className="text-xs text-faint">Nessun task. Pianifica un promemoria (data, ora, nota).</p>}
            </div>
          </Section>

          <Section title="Timeline attività">
            <div className="flex flex-col gap-3">
              {[...(o.activities || [])].reverse().map((a, i) => (
                <div key={i} className="flex gap-2.5">
                  <div className="mt-1 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                  <div>
                    <p className="text-xs text-ink">{a.text}</p>
                    <p className="text-[11px] text-faint">
                      {a.author ? `${a.author} · ` : ""}
                      {freshnessLabel(a.timestamp)}
                      {a.timestamp && !isNaN(new Date(a.timestamp)) ? ` · ${new Date(a.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Aggiungi nota">
            <textarea
              data-testid="note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
              placeholder="Scrivi una nota…"
            />
            <button data-testid="add-note-button" onClick={addNote} className="mt-2 w-full bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover transition-colors">
              Salva nota
            </button>
          </Section>
        </div>
      </div>

      {/* Close as lost dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="bg-white border-line rounded-[8px]">
          <DialogHeader><DialogTitle>Chiudi come Persa</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3">
            <label className="text-sm font-semibold">Motivo (obbligatorio)</label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lost-reason-select" className="bg-white border-line rounded-[6px]"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
              <SelectContent className="bg-white border-line">
                {lossReasons.map((r) => <SelectItem key={r.id} value={r.label}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <textarea data-testid="lost-note" value={lostNote} onChange={(e) => setLostNote(e.target.value)} rows={2} placeholder="Nota (opzionale)" className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
          </div>
          <DialogFooter>
            <button onClick={() => setLostOpen(false)} className="bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background transition-colors">Annulla</button>
            <button data-testid="confirm-lost" onClick={closeLost} className="bg-destructive text-white font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-[#E03B3B] transition-colors">Conferma perdita</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exclude dialog */}
      <Dialog open={excludeOpen} onOpenChange={setExcludeOpen}>
        <DialogContent className="bg-white border-line rounded-[8px]">
          <DialogHeader><DialogTitle>Escludi azienda</DialogTitle></DialogHeader>
          <textarea data-testid="exclude-reason" value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} rows={2} placeholder="Motivo esclusione" className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
          <DialogFooter>
            <button onClick={() => setExcludeOpen(false)} className="bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background transition-colors">Annulla</button>
            <button data-testid="confirm-exclude" onClick={excludeCompany} className="bg-destructive text-white font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-[#E03B3B] transition-colors">Escludi</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cold email dialog */}
      <Dialog open={emailModal.open} onOpenChange={(v) => setEmailModal((m) => ({ ...m, open: v }))}>
        <DialogContent data-testid="cold-email-dialog" className="bg-white border-line rounded-[8px] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-brand-dark" strokeWidth={1.5} /> Cold email — {emailModal.dmName}
            </DialogTitle>
            <DialogDescription>Email personalizzata generata dall'AI. Rivedila prima di inviarla.</DialogDescription>
          </DialogHeader>
          {emailModal.loading ? (
            <div className="flex items-center gap-2 text-sm text-subtle py-8 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Sto scrivendo l'email personalizzata…
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Oggetto</p>
                <input
                  data-testid="cold-email-subject"
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal((m) => ({ ...m, subject: e.target.value }))}
                  className="w-full text-sm font-medium text-ink bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
                />
              </div>
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Corpo (modificabile)</p>
                <textarea
                  data-testid="cold-email-body"
                  value={emailModal.body}
                  onChange={(e) => setEmailModal((m) => ({ ...m, body: e.target.value }))}
                  rows={12}
                  className="w-full text-sm text-ink bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors whitespace-pre-wrap"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="cold-email-regenerate" onClick={regenerateEmail} disabled={emailModal.loading} className="flex items-center gap-1.5 bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background disabled:opacity-50 transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${emailModal.loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> {emailModal.loading ? "Rigenero…" : "Rigenera"}
            </button>
            <button data-testid="cold-email-save" onClick={saveEmail} disabled={emailModal.loading || !emailModal.body} className="flex items-center gap-1.5 bg-white border border-brand-dark text-brand-dark font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand/10 disabled:opacity-50 transition-colors">
              <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Salva
            </button>
            <button data-testid="cold-email-draft" onClick={createDraft} disabled={draftLoading || emailModal.loading || !emailModal.body || !assigneeGmail.connected} title={assigneeGmail.connected ? `Crea bozza in ${assigneeGmail.email}` : "L'assegnatario deve collegare il proprio account Google (Profilo · Email)"} className="flex items-center gap-1.5 bg-white border border-line text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-background disabled:opacity-50 transition-colors">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {draftLoading ? "Creo bozza…" : "Crea bozza in Gmail"}
            </button>
            <button data-testid="cold-email-copy" onClick={copyEmail} disabled={emailModal.loading || !emailModal.body} className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
              <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copia
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual DM dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-dm-dialog" className="bg-white border-line rounded-[8px] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4 text-brand-dark" strokeWidth={1.5} /> Aggiungi Decision Maker</DialogTitle>
            <DialogDescription>Inserisci manualmente un contatto trovato dal Sales. Email/telefono sono opzionali.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Nome</p>
                <input data-testid="manual-dm-first" value={manualDm.first_name} onChange={(e) => setManualDm((m) => ({ ...m, first_name: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Cognome</p>
                <input data-testid="manual-dm-last" value={manualDm.last_name} onChange={(e) => setManualDm((m) => ({ ...m, last_name: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
            </div>
            <div>
              <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Ruolo</p>
              <input data-testid="manual-dm-role" value={manualDm.role} onChange={(e) => setManualDm((m) => ({ ...m, role: e.target.value }))} placeholder="Es. Titolare, Direttore Commerciale" className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
            </div>
            <div>
              <p className="text-[11px] text-faint uppercase tracking-wide mb-1">LinkedIn (opzionale)</p>
              <input data-testid="manual-dm-linkedin" value={manualDm.linkedin_url} onChange={(e) => setManualDm((m) => ({ ...m, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/in/…" className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Email (opzionale)</p>
                <input data-testid="manual-dm-email" value={manualDm.email} onChange={(e) => setManualDm((m) => ({ ...m, email: e.target.value }))} placeholder="nome@azienda.it" className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
              <div>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Telefono (opzionale)</p>
                <input data-testid="manual-dm-phone" value={manualDm.phone} onChange={(e) => setManualDm((m) => ({ ...m, phone: e.target.value }))} placeholder="+39…" className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setManualOpen(false)} className="bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background transition-colors">Annulla</button>
            <button data-testid="manual-dm-save" onClick={addManualDm} disabled={addingDm} className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {addingDm ? "Aggiungo…" : "Aggiungi"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit company dialog */}
      <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
        <DialogContent data-testid="company-edit-dialog" className="bg-white border-line rounded-[8px] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-brand-dark" strokeWidth={1.5} /> Modifica dati azienda</DialogTitle>
            <DialogDescription>Il Sito/Dominio è usato dalla ricerca automatica dei Decision Maker.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {[
              ["name", "Ragione sociale"], ["vat", "P.IVA"], ["industry", "Settore"],
              ["size", "Dimensione"], ["zone", "Zona"],
              ["website", "Sito (es. https://azienda.it)"], ["domain", "Dominio (es. azienda.it)"],
              ["linkedin_url", "LinkedIn azienda"],
            ].map(([k, lab]) => (
              <div key={k}>
                <p className="text-[11px] text-faint uppercase tracking-wide mb-1">{lab}</p>
                <input data-testid={`company-${k}`} value={companyForm[k] || ""} onChange={(e) => setCompanyForm((f) => ({ ...f, [k]: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
              </div>
            ))}
            <div>
              <p className="text-[11px] text-faint uppercase tracking-wide mb-1">Note</p>
              <textarea data-testid="company-notes" rows={3} value={companyForm.notes || ""} onChange={(e) => setCompanyForm((f) => ({ ...f, notes: e.target.value }))} className="w-full text-sm bg-white border border-line rounded-[6px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand transition-colors" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCompanyOpen(false)} className="bg-white border border-line text-ink text-sm rounded-[6px] px-4 py-2 hover:bg-background transition-colors">Annulla</button>
            <button data-testid="company-save" onClick={saveCompany} disabled={savingCompany} className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
              <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> {savingCompany ? "Salvo…" : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, link }) {
  return (
    <div>
      <p className="text-[11px] text-faint uppercase tracking-wide">{label}</p>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="text-sm text-brand-dark hover:underline inline-flex items-center gap-1">
          {value || "—"} <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
        </a>
      ) : (
        <p className="text-sm text-ink">{value || "—"}</p>
      )}
    </div>
  );
}

function SignalRow({ s }) {
  const [open, setOpen] = useState(false);
  const fresh = freshnessLabel(s.date_posted);
  return (
    <div className="border border-line rounded-[6px] p-3">
      <div className="flex items-start justify-between gap-3">
        <button onClick={() => setOpen(!open)} className="flex items-start gap-2 text-left flex-1">
          {open ? <ChevronDown className="w-4 h-4 text-subtle mt-0.5" strokeWidth={1.5} /> : <ChevronRight className="w-4 h-4 text-subtle mt-0.5" strokeWidth={1.5} />}
          <div>
            <p className="text-sm font-medium text-ink">{s.title}</p>
            <p className="text-xs text-subtle">{s.source} · <span className="text-brand-dark">{fresh}</span></p>
          </div>
        </button>
        {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-brand-dark shrink-0"><ExternalLink className="w-4 h-4" strokeWidth={1.5} /></a>}
      </div>
      {open && s.description && <p className="text-xs text-subtle mt-2 pl-6">{s.description}</p>}
    </div>
  );
}
